import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../lib/store';
import { Plus, X, Bike, DollarSign, FileText, Calendar, AlertTriangle, MapPin, Edit2, SortAsc, SortDesc, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { EditBikeModal } from './modals/EditBikeModal';

function BikeInventory() {
  const { bikes, fetchBikes, createBike, loading } = useStore();
  const [showNewBikeForm, setShowNewBikeForm] = useState(false);
  const [showEditBikeModal, setShowEditBikeModal] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<string | null>(null);
  const [sortField, setSortField] = useState('make');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    rego_number: '',
    rego_expiry: '',
    rate_a: '',
    rate_b: '',
    last_service: '',
    service_centre: '',
    service_notes: '',
  });
  const [bikeDeliveryAddresses, setBikeDeliveryAddresses] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchBikes();
    fetchDeliveryAddresses();
  }, []);

  const fetchDeliveryAddresses = async () => {
    try {
      const { data: cases, error } = await supabase
        .from('cases')
        .select('bike_id, delivery_address')
        .eq('status', 'bike_delivered');

      if (error) throw error;

      const addresses = (cases || []).reduce((acc, case_) => ({
        ...acc,
        [case_.bike_id]: case_.delivery_address || 'TBA'
      }), {});

      setBikeDeliveryAddresses(addresses);
    } catch (error) {
      console.error('Error fetching delivery addresses:', error);
    }
  };

  const handleDeleteBike = async (bikeId: string) => {
    try {
      // First check if bike is currently assigned to any case
      const { data: activeCase } = await supabase
        .from('cases')
        .select('id')
        .eq('bike_id', bikeId)
        .eq('status', 'bike_delivered')
        .maybeSingle();

      if (activeCase) {
        alert('Cannot delete bike that is currently assigned to an active case');
        return;
      }

      // Update bike status to decommissioned instead of deleting
      const { error } = await supabase
        .from('bikes')
        .update({ status: 'decommissioned' })
        .eq('id', bikeId);

      if (error) throw error;

      // Refresh bikes list
      fetchBikes();
      setShowDeleteConfirmation(null);
    } catch (error) {
      console.error('Error decommissioning bike:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const bikeData = {
        ...formData,
        rate_a: parseFloat(formData.rate_a),
        rate_b: parseFloat(formData.rate_b),
      };
      await createBike(bikeData);
      setShowNewBikeForm(false);
      setFormData({
        make: '',
        model: '',
        rego_number: '',
        rego_expiry: '',
        rate_a: '',
        rate_b: '',
        last_service: '',
        service_centre: '',
        service_notes: '',
      });
      fetchBikes();
    } catch (error) {
      console.error('Error creating bike:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getTotalDailyRates = () => {
    return bikes
      .filter(bike => bike.status === 'delivered')
      .reduce((total, bike) => total + (bike.rate_a || 0), 0);
  };

  const getExpiringRegos = () => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    
    return bikes.filter(bike => {
      if (!bike.rego_expiry) return false;
      const expiryDate = new Date(bike.rego_expiry);
      return expiryDate <= thirtyDaysFromNow;
    });
  };

  const formatServiceCentre = (centre: string) => {
    switch (centre) {
      case 'five_dock':
        return 'Five Dock';
      case 'seven_hills':
        return 'Seven Hills';
      case 'reza':
        return 'Reza';
      default:
        return 'Not set';
    }
  };

  const handleEditCase = async (e: React.MouseEvent, bikeId: string) => {
    e.stopPropagation();
    try {
      const { data: caseData, error } = await supabase
        .from('cases')
        .select('id')
        .eq('bike_id', bikeId)
        .eq('status', 'bike_delivered')
        .maybeSingle();

      if (error) throw error;

      if (caseData) {
        navigate(`/cases/${caseData.id}`);
      }
    } catch (error) {
      console.error('Error getting case:', error);
    }
  };

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedBikes = [...bikes].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortField) {
      case 'make':
        return (a.make || '').localeCompare(b.make || '') * direction;
      case 'registration':
        return (a.rego_number || '').localeCompare(b.rego_number || '') * direction;
      case 'rates':
        return ((a.rate_a || 0) - (b.rate_a || 0)) * direction;
      case 'service':
        return (formatServiceCentre(a.service_centre || '') || '').localeCompare(formatServiceCentre(b.service_centre || '') || '') * direction;
      case 'status':
        return (a.status || '').localeCompare(b.status || '') * direction;
      case 'delivery':
        return (bikeDeliveryAddresses[a.id] || '').localeCompare(bikeDeliveryAddresses[b.id] || '') * direction;
      default:
        return 0;
    }
  });

  const SortIndicator = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <SortAsc className="h-4 w-4 inline-block ml-1" /> : 
      <SortDesc className="h-4 w-4 inline-block ml-1" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#6563ff]"></div>
      </div>
    );
  }

  const expiringRegos = getExpiringRegos();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-gray-900">
          Bike Management
        </h1>
        <button
          onClick={() => setShowNewBikeForm(true)}
          className="inline-flex items-center px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add New Bike
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <DollarSign className="h-8 w-8 text-[#6563ff]" />
            <span className="text-2xl font-bold">${getTotalDailyRates().toFixed(2)}</span>
          </div>
          <h3 className="text-lg font-semibold">Total Daily Rates</h3>
          <p className="text-sm text-gray-600">For bikes currently on road</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Bike className="h-8 w-8 text-[#6563ff]" />
            <span className="text-2xl font-bold">
              {bikes.filter(b => b.status === 'delivered').length}
            </span>
          </div>
          <h3 className="text-lg font-semibold">Bikes on Road</h3>
          <p className="text-sm text-gray-600">Currently in use</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Bike className="h-8 w-8 text-[#6563ff]" />
            <span className="text-2xl font-bold">
              {bikes.filter(b => b.status === 'available').length}
            </span>
          </div>
          <h3 className="text-lg font-semibold">Available Bikes</h3>
          <p className="text-sm text-gray-600">Ready for assignment</p>
        </div>
      </div>

      {expiringRegos.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-yellow-800">Registration Expiring Soon</h3>
              <div className="mt-2 space-y-1">
                {expiringRegos.map(bike => (
                  <p key={bike.id} className="text-sm text-yellow-700">
                    {bike.make} {bike.model} ({bike.rego_number}) - Expires {new Date(bike.rego_expiry).toLocaleDateString()}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Bike Fleet</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => toggleSort('make')}
                >
                  Bike Details
                  <SortIndicator field="make" />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => toggleSort('registration')}
                >
                  Registration
                  <SortIndicator field="registration" />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => toggleSort('rates')}
                >
                  Rates
                  <SortIndicator field="rates" />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => toggleSort('service')}
                >
                  Service Centre
                  <SortIndicator field="service" />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => toggleSort('status')}
                >
                  Status
                  <SortIndicator field="status" />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => toggleSort('delivery')}
                >
                  Delivery Address
                  <SortIndicator field="delivery" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedBikes.map(bike => (
                <tr 
                  key={bike.id} 
                  onClick={() => setShowEditBikeModal(bike.id)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{bike.make} {bike.model}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{bike.rego_number}</div>
                    <div className="text-sm text-gray-500">
                      Expires: {bike.rego_expiry ? new Date(bike.rego_expiry).toLocaleDateString() : 'Not set'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">Rate A: ${bike.rate_a?.toFixed(2)}</div>
                    <div className="text-sm text-gray-900">Rate B: ${bike.rate_b?.toFixed(2)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatServiceCentre(bike.service_centre)}
                    </div>
                    <div className="text-sm text-gray-500">
                      Last Service: {bike.last_service ? new Date(bike.last_service).toLocaleDateString() : 'Not set'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      bike.status === 'available' ? 'bg-green-100 text-green-800' :
                      bike.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                      bike.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {bike.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {bike.status === 'delivered' ? (
                      <button
                        onClick={(e) => handleEditCase(e, bike.id)}
                        className="flex items-center text-[#6563ff] hover:text-[#5451ff] transition-colors"
                      >
                        <MapPin className="h-4 w-4 mr-2" />
                        {bikeDeliveryAddresses[bike.id] || 'TBA'}
                      </button>
                    ) : (
                      <span className="text-sm text-gray-500">Not assigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteConfirmation(bike.id);
                        }}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Delete Bike"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showNewBikeForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Add New Bike</h2>
              <button onClick={() => setShowNewBikeForm(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make
                </label>
                <input
                  type="text"
                  name="make"
                  value={formData.make}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model
                </label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Number
                </label>
                <input
                  type="text"
                  name="rego_number"
                  value={formData.rego_number}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Expiry
                </label>
                <input
                  type="date"
                  name="rego_expiry"
                  value={formData.rego_expiry}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate A ($)
                  </label>
                  <input
                    type="number"
                    name="rate_a"
                    value={formData.rate_a}
                    onChange={handleChange}
                    required
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate B ($)
                  </label>
                  <input
                    type="number"
                    name="rate_b"
                    value={formData.rate_b}
                    onChange={handleChange}
                    required
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Service
                </label>
                <input
                  type="date"
                  name="last_service"
                  value={formData.last_service}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Centre
                </label>
                <select
                  name="service_centre"
                  value={formData.service_centre}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                >
                  <option value="">Select a service centre</option>
                  <option value="five_dock">Five Dock</option>
                  <option value="seven_hills">Seven Hills</option>
                  <option value="reza">Reza</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Notes
                </label>
                <textarea
                  name="service_notes"
                  value={formData.service_notes}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewBikeForm(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors"
                >
                  Add Bike
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditBikeModal && (
        <EditBikeModal
          bikeId={showEditBikeModal}
          onClose={() => setShowEditBikeModal(null)}
          onBikeUpdated={() => {
            setShowEditBikeModal(null);
            fetchBikes();
          }}
        />
      )}

      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Delete Bike</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this bike? This action cannot be undone and the bike will be marked as decommissioned.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirmation(null)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteBike(showDeleteConfirmation)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Bike
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BikeInventory;